import React from 'react';

import { Button } from 'components/Button';
import { UniformThumb } from './UniformRow';
import { describeWindow } from './sizeWindow';

/**
 * "Your sizes" — what the employee asked for, what they were handed, and
 * whether they can still change it.
 *
 * This is the half of the uniform story the page could not tell before. The
 * issuance list answers "what do I hold"; only the profile answers "what size
 * am I on file for", and those two legitimately differ whenever the warehouse
 * substituted. Showing only the issued size made a substitution look like the
 * employee's own choice, which is how a wrong size survives a whole rotation.
 *
 * The uniforms being asked about come first and carry the deadline, because a
 * window that closes is the only thing on this screen with a clock on it.
 */

const SizeRow = ({ row, onChange, busy }) => {
  const window = describeWindow(row);

  return (
    <div className="myu-row myu-size-row">
      <UniformThumb item={row} />

      <div className="myu-body">
        <p className="myu-name">{row.uniform_item_name}</p>
        {/* The preference alone. It used to sit beside "issued XXL" whenever
            the two differed, which reads as a mistake but has two causes that
            cannot be told apart here: the warehouse substituted at issuance,
            or the employee changed their size afterwards — the very thing
            this screen asks them to do. Flagging the second as a discrepancy
            makes correct behaviour look broken. */}
        <p className="myu-meta">
          <span className="myu-meta-part">
            Your size {row.preferred_size_code || '—'}
          </span>
        </p>
        {window?.detail && <p className="myu-meta myu-size-detail">{window.detail}</p>}
      </div>

      <div className="myu-aside">
        {window && (
          <span className={`myu-due myu-window-${window.tone}`}>{window.text}</span>
        )}
        {row.can_change && (
          <Button
            type="button"
            // Primary only while an answer is actually wanted. Outside the
            // window this is a background edit, and a page of primary
            // buttons tells the employee nothing about which one matters.
            className={`btn btn-sm myu-size-btn ${
              row.needs_confirmation ? 'btn-primary' : 'btn-outline-secondary'
            }`}
            disabled={busy}
            onClick={() => onChange(row)}
            label={row.needs_confirmation ? 'Confirm size' : 'Change size'}
          />
        )}
      </div>
    </div>
  );
};

const SizeSection = ({ rows, onChange, busyItem }) => {
  if (!rows || rows.length === 0) return null;

  const asking = rows.filter(row => row.needs_confirmation);
  const rest = rows.filter(row => !row.needs_confirmation);

  return (
    <>
      {asking.length > 0 && (
        <section className="myu-section myu-pending" aria-labelledby="myu-size-ask-title">
          <h4 className="myu-section-title" id="myu-size-ask-title">
            <i className="fa fa-tag" aria-hidden="true" />
            {asking.length === 1
              ? 'Confirm your size for 1 uniform'
              : `Confirm your size for ${asking.length} uniforms`}
          </h4>
          <p className="myu-section-note">
            Your next uniform is ordered in the size saved here. Keep it or
            change it before the date shown.
          </p>
          {asking.map(row => (
            <SizeRow
              key={row.uniform_item}
              row={row}
              onChange={onChange}
              busy={busyItem === row.uniform_item}
            />
          ))}
        </section>
      )}

      {rest.length > 0 && (
        <section className="myu-section" aria-labelledby="myu-size-title">
          <h4 className="myu-section-title" id="myu-size-title">Your sizes</h4>
          <div className="myu-list myu-list-wide">
            {rest.map(row => (
              <SizeRow
                key={row.uniform_item}
                row={row}
                onChange={onChange}
                busy={busyItem === row.uniform_item}
              />
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default SizeSection;
